import React, { useCallback, useEffect, useState } from "react";
import queryString from "query-string";
import { func, node, shape, object } from "prop-types";
import * as R from "ramda";
import { fieldTypes } from "../../../common/useSearchValues.js";
import { getPageUrl, loadInPage } from "../../../common/queryStringBuilder.js";

/**
 * QueryString store.
 * v3.0.0
 * exports createStore, QueryStringContext, QueryStringProvider, QueryStringConsumer
 *
 * The url is the source of truth, so the state is not stored in this store.
 * Instead, use getStateFromUrl() whenever we need the state.
 * The store is mostly to keep track of the listeners, since there is no way to
 * detect a history.pushState in javascript. Using the store allows multiple
 * QueryStringProviders to stay in sync.
 *
 * Usage for Context provider:
 *   import { createStore, QueryStringProvider } from "./QueryStringContext.jsx";
 *
 *   const queryStringStore = createStore(defaultState);
 *
 *   <QueryStringProvider store={queryStringStore}>
 *     <App />
 *   </QueryStringProvider>
 *
 *
 * Usage for Context Consumer:
 *  import { useContext } from "react";
 *  import { QueryStringContext } from "./QueryStringContext.jsx";
 *
 *  const { queryStringState, setQueryStringState } = useContext(QueryStringContext);
 *
 * or
 *
 *  import { QueryStringConsumer } from "./QueryStringContext.jsx";
 *
 *  <QueryStringConsumer>
 *    ({ queryStringState, setQueryStringState }) => { ... }
 *  </QueryStringConsumer>
 *
 */

export function createStore(defaultState, model) {
  let listeners = [];


  function alertListeners() {
    listeners.forEach((l) => l());
  }

  function queryStateReducer(state) {
    return R.reduce(
      (obj, key) =>
        state[key] !== defaultState[key] ? { ...obj, [key]: state[key] } : obj,
      {},
    )(Object.keys(state));
  }

  // the url contains only state that is different from the defaults
  function getStateFromUrl() {
    /*
    query-string will pass a string when only 1 exists, backend requires
    brackets added to all multi-select fields regardless of value(s) we are
    checking fields against fieldtype object to set required fieldtype
    */

    const parse = queryString.parse(window.location.search, {
      arrayFormat: "index",
      skipEmptyString: true,
      skipNull: true,
      parseNumbers: false,
    });

    const updatedState = R.mapObjIndexed((v, k, o) =>
      fieldTypes?.[k] === "Array" ? R.unnest([v]) : v,
    )(parse);
    return { ...defaultState, ...updatedState };
  }

  function getUrl(state) {
    //queryStateReducer strips default values so we can keep them out of query string
    return (
      window.location.origin +
      window.location.pathname +
      "?" +
      queryString.stringify(queryStateReducer(state), {
        arrayFormat: "index",
        skipEmptyString: true,
        skipNull: true,
      })
    );
  }

  // public function that the consumers will call
  function setQueryStringState(newState) {
    // merge the new state in with the current state
    const state = {
      ...getStateFromUrl(),
      ...newState,
    };

    // add a new entry to the browser history
    history.pushState(null, null, getUrl(state));

    if (loadInPage()) {
      alertListeners();
    }
    else {
      window.location.href = getPageUrl('1', model, state);
    }
  }

  // popstate listener
  function handleUrlChange() {
    // replaceState to update the url to have all the default values
    // from the defaultState
    history.replaceState(null, null, getUrl(getStateFromUrl()));
    alertListeners();
  }

  // subscribe to query string changes
  function subscribe(callback) {
    listeners.push(callback);

    // only listen to popstate if we have listners
    if (listeners.length > 0) {
      // when the user navigates the history via back/forward
      window.addEventListener("popstate", handleUrlChange);
    }

    return function unsubscribe() {
      listeners = listeners.filter((l) => l === callback);
      if (listeners.length === 0) {
        // if we don't have any listners, remove popstate event listener
        window.removeEventListener("popstate", handleUrlChange);
      }
    };
  }

  return {
    getState: getStateFromUrl,
    setState: setQueryStringState,
    reducer: queryStateReducer,
    model: model,
    subscribe,
  };
}

export const QueryStringContext = React.createContext();

export const QueryStringProvider = (props) => {
  const { children, store } = props;

  const getProviderValue = useCallback(
    () => ({
      queryStringState: store.getState(),
      setQueryStringState: store.setState,
      queryStateReducer: store.reducer,
      model: store.model,
    }),
    [store],
  );

  const [providerValue, setProviderValue] = useState(getProviderValue());

  useEffect(() => {
    // store.subscribe returns an unsubscribe function
    return store.subscribe(() => {
      setProviderValue(getProviderValue());
    });
  }, [getProviderValue, store]);

  return (
    <QueryStringContext.Provider value={providerValue}>
      {children}
    </QueryStringContext.Provider>
  );
};

QueryStringProvider.propTypes = {
  children: node,
  store: shape({
    getState: func.isRequired,
    setState: func.isRequired,
    reducer: func.isRequired,
    model: object,
    subscribe: func.isRequired,
  }).isRequired,
};

export const QueryStringConsumer = QueryStringContext.Consumer;
